/**
 * Module-scope custody of Chrome's deferred `beforeinstallprompt` event (T52 iteration 4).
 *
 * WHY THIS IS NOT REACT STATE
 * ---------------------------
 * It was, and that was the bug. `PwaProvider` held the captured event in `useState`, and
 * `app/root.tsx` mounts that provider **conditionally** — it stands down with the rest of
 * the chrome on the immersive player. So entering a session unmounted the provider and
 * destroyed the event with it. The browser does not re-fire `beforeinstallprompt` on a
 * client-side navigation; only a full document load does. On the way back to the dashboard
 * the provider remounted with nothing.
 *
 * Finishing a ritual happens *inside* the player, and finishing a ritual is exactly what
 * arms the card (`hasCompletedRitual()`). So the one action that made the card eligible was
 * the same action that guaranteed its trigger was gone. The founder found it and named the
 * mechanism herself, by noticing that switching language — a full document load — brought
 * the card back.
 *
 * The event therefore has to outlive any component. It lives here.
 *
 * THE SECOND HOLE, CLOSED BY THE SAME MOVE
 * ----------------------------------------
 * The old listener was registered inside a `useEffect`, i.e. after hydration. Chrome fires
 * `beforeinstallprompt` early in page load, and an event fired before the listener exists is
 * lost for that load with no second chance — a race whose outcome depended on how heavy the
 * page was. Registering at module-evaluation time (this file is imported by the provider,
 * which is imported by the root route, so it runs during bundle evaluation) means the
 * listener is installed before React mounts anything.
 *
 * Read it from React with `useSyncExternalStore` — `subscribe` + `getSnapshot` below are
 * shaped for exactly that.
 */

/** Not in lib.dom — Chromium-only, and the reason iOS needs the static instructions. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Registered once, at import time. Guarded for SSR — this module is reachable from the
 * server render, where there is no `window` and nothing to listen to.
 */
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppress the browser's own bar; the contract says we choose the moment (§5).
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    emit();
  });

  window.addEventListener("appinstalled", () => {
    deferred = null;
    emit();
  });
}

/**
 * `getSnapshot` for `useSyncExternalStore`. Returns the same reference until the event
 * actually changes, which is what stops React from looping.
 */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

/** `getServerSnapshot` — there is no deferred event during SSR, by construction. */
export function getServerInstallPrompt(): null {
  return null;
}

export function subscribeToInstallPrompt(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/**
 * Spend the event. A `BeforeInstallPromptEvent` can only be `prompt()`ed once, so holding
 * a used one would leave the card offering something that no longer works.
 */
export function clearInstallPrompt(): void {
  deferred = null;
  emit();
}
